import { UntilDestroy } from '@ngneat/until-destroy';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { selectPageRouteMap } from 'sharedStores/cms/cms.selectors';
import { selectQuoteFlowType } from 'stores/quote/quote.selectors';
import { QuoteFlowType } from 'sharedEnums/quote-flow-type';
import { ConditionalPageActivationResult } from 'services/navigation/navigation.service';
import { PageRouteMapKeys } from 'sharedModels/page-route-map';

/**
 * Service for handling navigation logic related to "Work Days From Home".
 * This service resolves conditional page activation based on the current quote flow type
 * and the page name provided.
 */
@UntilDestroy()
@Injectable({
    providedIn: 'root',
})
export class WorkDaysFromHomeNavigationService {
    /**
     * Constructor for the service.
     * @param store$ - The NgRx store used to select state signals.
     */
    public constructor(private readonly store$: Store) {}

    /**
     * Signal for retrieving the page route map from the store.
     */
    private readonly pageRouteMap = this.store$.selectSignal(selectPageRouteMap);

    /**
     * Signal for retrieving the current quote flow type from the store.
     */
    private readonly quoteFlowType = this.store$.selectSignal(selectQuoteFlowType);

    /**
     * Resolves conditional page activation based on the provided page name.
     * Handles "WorkdaysFromHome" and "HouseholdMemberWorkdaysFromHome" page names
     * @param pageName - The name of the page to resolve activation for.
     * @returns A `ConditionalPageActivationResult` containing the activation status and route.
     */
    public resolveConditionalPageActivation(pageName: string): ConditionalPageActivationResult {
        const map = this.pageRouteMap();

        // Determine the route based on the page name.
        const route =
            pageName == PageRouteMapKeys.WorkdaysFromHome
                ? map[PageRouteMapKeys.IsRegularDriver]
                : null;

        // Default return value with activation set to false.
        let returnValue = new ConditionalPageActivationResult(false, route);

        // Update the return value if specific conditions are met.
        if (
            pageName == PageRouteMapKeys.WorkdaysFromHome &&
            this.quoteFlowType != null &&
            this.quoteFlowType() === QuoteFlowType.HomeContents
        ) {
            returnValue = new ConditionalPageActivationResult(
                false,
                map[PageRouteMapKeys.HomeContentReplacementValue],
            );
        }

        return returnValue;
    }
}
